import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Card, Icon, Input, Toast } from '@atoms/index';
import { ChangePasswordModal } from '@organisms/index';
import { useAuthStore } from '@store/authStore';
import { addressService } from '@services/addressService';
import type { UserAddress } from '@/types';

const ProfilePage = () => {
  const location = useLocation();
  const { user, updateProfile } = useAuthStore();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [defaultAddress, setDefaultAddress] = useState<UserAddress | null>(null);
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [toast, setToast] = useState<{ isVisible: boolean; message: string; type: 'success' | 'error' }>({ isVisible: false, message: '', type: 'success' });

  useEffect(() => {
    if (user) {
      setPhone(user.phone || '');
      setEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const addresses = await addressService.getAddresses();
        const def = addresses.find((a) => a.isDefault) || addresses[0];
        if (!cancelled) setDefaultAddress(def || null);
      } catch {
        if (!cancelled) setDefaultAddress(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [location.key]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsProfileSaving(true);
    try {
      await updateProfile({
        phone: phone.trim() || null,
        email: email.trim() || undefined,
      });
      setToast({ isVisible: true, message: 'Profil güncellendi.', type: 'success' });
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || 'Profil güncellenirken bir hata oluştu.';
      setToast({ isVisible: true, message: msg, type: 'error' });
    } finally {
      setIsProfileSaving(false);
    }
  };

  const zipFromAddress = (addr: UserAddress | null): string => {
    if (!addr?.fullAddress) return '01000';
    const m = addr.fullAddress.match(/\b\d{5}\b/);
    return m ? m[0] : '01000';
  };

  if (!user) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <p className="text-gray-500">Profil yükleniyor...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Profilim</h1>
        <p className="text-gray-500 text-sm mb-8">Kişisel bilgilerinizi ve hesap ayarlarınızı yönetin</p>

        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((t) => ({ ...t, isVisible: false }))}
        />

        <form onSubmit={handleSaveProfile} className="space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="user" size={16} className="text-primary" />
              </div>
              İletişim Bilgileri
            </h2>
            <div className="space-y-4">
              <Input
                label="Telefon"
                type="tel"
                placeholder="05551234567 veya +905551234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                helperText="Ödeme ve teslimat için kullanılır. Profilinizden istediğiniz zaman güncelleyebilirsiniz."
              />
              <Input
                label="E-posta"
                type="email"
                placeholder="ornek@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              disabled={isProfileSaving}
              isLoading={isProfileSaving}
              className="mt-4"
            >
              Kaydet
            </Button>
          </Card>
        </form>

        <Card className="p-6 mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="location" size={16} className="text-primary" />
            </div>
            Adres Bilgisi
          </h2>
          <p className="text-gray-600 text-sm mb-2">
            Teslimat adreslerinizi yönetin. Ödeme sırasında varsayılan adresinizdeki posta kodu kullanılır.
          </p>
          {defaultAddress ? (
            <div className="p-4 bg-gray-50 rounded-xl mb-4">
              <p className="font-medium text-gray-900">{defaultAddress.title}</p>
              <p className="text-sm text-gray-600 mt-1">{defaultAddress.fullAddress}</p>
              <p className="text-xs text-gray-500 mt-1">
                Posta kodu: {zipFromAddress(defaultAddress)}
              </p>
            </div>
          ) : (
            <p className="text-gray-500 text-sm mb-4">Henüz kayıtlı adresiniz yok.</p>
          )}
          <Link to="/addresses">
            <Button variant="secondary" className="w-full sm:w-auto">
              <Icon name="location" size={18} className="mr-2" />
              Adresleri Yönet
            </Button>
          </Link>
        </Card>

        <Card className="p-6 mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Icon name="shield" size={16} className="text-amber-600" />
            </div>
            Şifre Değiştir
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            Hesap güvenliğiniz için düzenli aralıklarla şifrenizi güncelleyebilirsiniz.
          </p>
          <Button
            variant="secondary"
            onClick={() => setIsPasswordModalOpen(true)}
          >
            Şifreyi Değiştir
          </Button>
        </Card>
      </div>

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={() => {
          setIsPasswordModalOpen(false);
          setToast({ isVisible: true, message: 'Şifreniz başarıyla güncellendi.', type: 'success' });
        }}
      />
    </MainLayout>
  );
};

export default ProfilePage;
